/**
 * Disposable teaching data for the Cost Price tour.
 *
 * These rows are seeded into the cost-price / subrecipe / order-history stores while a
 * practice branch runs and removed when it ends. They never reach the API: every write
 * the real editor issues against a `onboarding-practice-` id is intercepted in
 * `practiceGuards.ts` and applied to an in-memory copy instead.
 *
 * Prices are stated the way the order-history store hands them to the picker, i.e. AFTER
 * `adjustOrderProductPrices` — the seed bypasses that pass, so `platform` must stay off
 * `platformsWithPerKgPrice` and `quantityInfo` must need no rescale (dataRules rule 2/3).
 * `any_invoice` is deliberate: it is the generic-invoice platform and it lives in
 * `platformsWithoutArticleId`, so product identity is the name, which keeps the fixture
 * stable no matter what the user's real catalogue contains.
 */
// The bare singleton, not the app's bootstrap: the fixture is reached from the picker
// and from the tour, and neither should drag i18next initialisation in behind it.
import i18next from 'i18next';
import { Platform } from '../../../types/Platforms';
import { Unit, type EmailProductData, type OrderHistory } from '../../../stores/appOrderHistoryStore';
import type { CostPriceAnalysis } from '../../../services/costPrice/costPriceService';
import type { RevenueHistory } from '../../../services/revenuehistory/revenueHistoryService';
import { RevenueSourceType } from '../../../types/RevenueSourceType';
import { PRACTICE_DISH_ID, PRACTICE_ID_PREFIX, PRACTICE_ORDER_ID } from './practiceGuards';

export const PRACTICE_PLATFORM = Platform.ANY_INVOICE;

const FOOD_VAT_MULTIPLIER = 1.09;

/** The first three products retain the walkthrough's exact portion prices. */
const PRACTICE_PRODUCT_SPECS = [
  { key: 'lamb', orderedQuantity: 5, articleId: `${PRACTICE_ID_PREFIX}lamb`, price: 48, stdAmount: 2000, category: 'MEAT' },
  { key: 'butter', orderedQuantity: 7, articleId: `${PRACTICE_ID_PREFIX}butter`, price: 8, stdAmount: 1000, category: 'DAIRY' },
  { key: 'garlic', orderedQuantity: 2, articleId: `${PRACTICE_ID_PREFIX}garlic`, price: 6, stdAmount: 1000, category: 'VEGETABLES' },
  { key: 'chicken', orderedQuantity: 10, articleId: `${PRACTICE_ID_PREFIX}chicken`, price: 20, stdAmount: 2000, category: 'MEAT' },
  { key: 'rice', orderedQuantity: 6, articleId: `${PRACTICE_ID_PREFIX}rice`, price: 4, stdAmount: 1000, category: 'GRAINS' },
  { key: 'mushrooms', orderedQuantity: 13, articleId: `${PRACTICE_ID_PREFIX}mushrooms`, price: 15, stdAmount: 1000, category: 'VEGETABLES' },
  { key: 'salmon', orderedQuantity: 5, articleId: `${PRACTICE_ID_PREFIX}salmon`, price: 33, stdAmount: 1000, category: 'FISH' },
  { key: 'potatoes', orderedQuantity: 36, articleId: `${PRACTICE_ID_PREFIX}potatoes`, price: 3, stdAmount: 1000, category: 'VEGETABLES' },
] as const;

export type PracticeProductKey = (typeof PRACTICE_PRODUCT_SPECS)[number]['key'];

function productName(key: PracticeProductKey): string {
  return i18next.t(`onboarding.costPrice.practice.products.${key}`);
}

export function buildPracticeProducts(): EmailProductData[] {
  return PRACTICE_PRODUCT_SPECS.map(spec => ({
    articleId: spec.articleId,
    name: productName(spec.key),
    quantity: 1,
    rawQuantity: 1,
    price: spec.price,
    priceWithTaxes: Number((spec.price * FOOD_VAT_MULTIPLIER).toFixed(2)),
    taxRate: 9,
    category: spec.category,
    mainCategory: 'FOOD',
    quantityInfo: {
      weightString: `${spec.stdAmount} ${Unit.GRAM}`,
      stdAmount: spec.stdAmount,
      stdMultiplier: 1,
      stdUnit: Unit.GRAM,
    },
  }));
}

/** Dated today so every fixture product appears in the picker's 60-day counter. */
export function buildPracticeOrder(restaurantId: string): OrderHistory {
  const products = buildPracticeProducts().map((product, index) => ({
    ...product,
    quantity: PRACTICE_PRODUCT_SPECS[index].orderedQuantity,
    rawQuantity: PRACTICE_PRODUCT_SPECS[index].orderedQuantity,
  }));
  return {
    id: PRACTICE_ORDER_ID,
    restaurantPlatformId: `${PRACTICE_ID_PREFIX}platform`,
    messageId: `${PRACTICE_ID_PREFIX}message`,
    gmailId: `${PRACTICE_ID_PREFIX}gmail`,
    platform: PRACTICE_PLATFORM,
    clientName: i18next.t('onboarding.costPrice.practice.supplier'),
    orderNumber: `${PRACTICE_ID_PREFIX}1`,
    orderDate: new Date(),
    total: products.reduce((sum, product) => sum + product.price * product.quantity, 0),
    totalWithTaxes: products.reduce((sum, product) => sum + (product.priceWithTaxes ?? product.price) * product.quantity, 0),
    productData: products,
    restaurantId,
    unknownSupplierName: i18next.t('onboarding.costPrice.practice.supplier'),
  };
}

/**
 * The dish the branches build up. Starts with no ingredients so `cost-ing-zero` has a
 * genuinely empty dish to point at, and carries a custom menu price so the margin and
 * cost-% readouts in the drawer are real numbers rather than dashes.
 */
export function buildPracticeDish(restaurantId: string, overview = false): CostPriceAnalysis {
  return buildDish(restaurantId, PRACTICE_DISH_ID, 'dishName', 28,
    overview ? { lamb: 200, butter: 30, garlic: 10, potatoes: 180 } : {});
}

const EXAMPLE_DISHES = [
  { key: 'chicken', price: 18.5, sales: 84, ingredients: { chicken: 180, potatoes: 200, butter: 20, garlic: 5 } },
  { key: 'risotto', price: 16.5, sales: 62, ingredients: { rice: 80, mushrooms: 160, butter: 30, garlic: 10 } },
  { key: 'salmon', price: 16, sales: 24, ingredients: { salmon: 180, potatoes: 180, butter: 20, garlic: 5 } },
  // The utilization exercise adds the missing lamb to this starter.
  { key: 'lambStarter', price: 12, sales: 16, ingredients: { mushrooms: 40, butter: 5 } },
] as const;

/** Calculated examples stay visible while the learner builds the empty practice dish. */
export function buildPracticeExampleDishes(restaurantId: string, includeLambStarter = false): CostPriceAnalysis[] {
  return EXAMPLE_DISHES.filter(spec => includeLambStarter || spec.key !== 'lambStarter').map(spec => buildDish(restaurantId, `${PRACTICE_ID_PREFIX}${spec.key}-dish`,
    `dishes.${spec.key}`, spec.price, spec.ingredients));
}

function buildDish(
  restaurantId: string,
  id: string,
  nameKey: string,
  price: number,
  quantities: Partial<Record<PracticeProductKey, number>>,
): CostPriceAnalysis {
  const now = new Date();
  const products = buildPracticeProducts();
  const name = i18next.t(`onboarding.costPrice.practice.${nameKey}`);
  return {
    id,
    restaurantId,
    costPriceName: name,
    menuItemName: name,
    ingredients: PRACTICE_PRODUCT_SPECS.flatMap((spec, index) => {
      const quantityNeeded = quantities[spec.key];
      if (!quantityNeeded) return [];
      const product = products[index];
      return [{ id: `${id}-${spec.key}`, articleId: product.articleId, productName: product.name,
        platform: PRACTICE_PLATFORM, quantityNeeded, quantityInfo: product.quantityInfo }];
    }),
    totalCost: 0,
    customMenuPrice: price,
    customMenuPriceExVat: Number((price / FOOD_VAT_MULTIPLIER).toFixed(2)),
    isCustomItem: true,
    customItemPrice: price,
    customItemPriceExVat: Number((price / FOOD_VAT_MULTIPLIER).toFixed(2)),
    createdAt: now,
    updatedAt: now,
  };
}

/** Linked sales in both stages; changing a menu price does not rewrite past revenue. */
export function buildPracticeRevenue(restaurantId: string, dishes: CostPriceAnalysis[]): RevenueHistory[] {
  const items = dishes.flatMap(dish => {
    if (!dish.menuItemName) return [];
    const example = EXAMPLE_DISHES.find(spec => dish.id === `${PRACTICE_ID_PREFIX}${spec.key}-dish`);
    const quantity = example?.sales ?? 40;
    const price = Number(((dish.customItemPrice ?? 28) * quantity).toFixed(2));
    return [{ name: dish.menuItemName, quantity, price, exVat: Number((price / FOOD_VAT_MULTIPLIER).toFixed(2)) }];
  });
  if (items.length === 0) return [];
  return [{
    id: `${PRACTICE_ID_PREFIX}revenue`, messageId: '', gmailId: '',
    restaurantRevenueSourceId: `${PRACTICE_ID_PREFIX}source`, restaurantId,
    date: new Date(), revenueSource: 'practice', revenueSourceType: RevenueSourceType.FOOD,
    revenueData: { categories: [{ name: 'FOOD',
      quantity: items.reduce((sum, item) => sum + item.quantity, 0),
      price: Number(items.reduce((sum, item) => sum + item.price, 0).toFixed(2)),
      exVat: Number(items.reduce((sum, item) => sum + item.exVat, 0).toFixed(2)),
      items,
    }] },
  }];
}
